import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, Users, BarChart3, Upload, AlertTriangle, Settings,
  ChevronLeft, Building, Mail, Shield, Check, ArrowRight, HelpCircle,
  FileSpreadsheet, Bell, Eye, TrendingUp
} from "lucide-react";

const AdminManualPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const sections = [
    { id: "overview", title: "Vue d'ensemble", icon: BarChart3, color: "text-[#1A365D]" },
    { id: "students", title: "Eleves", icon: Users, color: "text-[#3B82F6]" },
    { id: "import", title: "Import", icon: Upload, color: "text-[#22C55E]" },
    { id: "alerts", title: "Moderation", icon: AlertTriangle, color: "text-[#EF4444]" },
    { id: "settings", title: "Reglages", icon: Settings, color: "text-[#8B5CF6]" }
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-manual-page">
      {/* Header */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#1A365D]"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">Mode d'emploi Admin</span>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-5xl mx-auto page-enter">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Guide Administrateur SophIA
          </h1>
          <p className="text-[#64748B]">
            Tout ce que vous devez savoir pour gerer votre etablissement sur SophIA
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent h-auto mb-8">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <TabsTrigger
                  key={section.id}
                  value={section.id}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all data-[state=active]:bg-[#1A365D] data-[state=active]:text-white data-[state=active]:border-[#1A365D] data-[state=inactive]:border-[#E2E8F0] data-[state=inactive]:bg-white`}
                >
                  <Icon className={`w-4 h-4 ${activeTab === section.id ? 'text-white' : section.color}`} />
                  <span className="hidden sm:inline">{section.title}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <BarChart3 className="w-6 h-6 text-[#1A365D]" />
                  Tableau de bord
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-[#64748B] leading-relaxed">
                  Le tableau de bord vous offre une vue synthetique de l'activite de votre etablissement. 
                  Vous y trouverez les indicateurs cles et pourrez agir rapidement sur les alertes.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#F8F9FA] rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#22C55E]" />
                      Indicateurs disponibles
                    </h3>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Nombre d'eleves actifs cette semaine
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Exercices completes (dissertations, commentaires)
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Score moyen des eleves
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Alertes de moderation en attente
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-[#1A365D]/5 rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <Eye className="w-4 h-4 text-[#3B82F6]" />
                      Graphiques et analyses
                    </h3>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Evolution hebdomadaire de l'activite
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Repartition par type d'exercice
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Classement des meilleurs eleves
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Management */}
          <TabsContent value="students">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Users className="w-6 h-6 text-[#3B82F6]" />
                  Gestion des eleves
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-[#64748B] leading-relaxed">
                  Suivez la progression de chaque eleve, identifiez ceux qui ont besoin d'aide, 
                  et celebrez les reussites.
                </p>

                <div className="space-y-4">
                  <div className="p-4 bg-[#3B82F6]/10 rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2">Liste des eleves</h3>
                    <p className="text-sm text-[#64748B] mb-2">
                      La liste affiche tous les eleves avec leurs informations cles :
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Nom et prenom
                      </div>
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Classe
                      </div>
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Points accumules
                      </div>
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Niveau actuel
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-[#F8F9FA] rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2">Filtres disponibles</h3>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <strong>Par classe</strong> : Terminale A, B, C, CPGE...
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <strong>Par activite</strong> : Actifs, Inactifs cette semaine
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <strong>Recherche</strong> : Par nom ou prenom
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Import */}
          <TabsContent value="import">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <FileSpreadsheet className="w-6 h-6 text-[#22C55E]" />
                  Import en masse
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-[#64748B] leading-relaxed">
                  Importez facilement vos listes d'eleves depuis Excel ou CSV. 
                  Le systeme cree automatiquement les comptes.
                </p>

                <div className="p-4 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/20">
                  <h3 className="font-semibold text-[#1A365D] mb-3">Format du fichier</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#E2E8F0]">
                          <th className="text-left py-2 text-[#1A365D]">Colonne</th>
                          <th className="text-left py-2 text-[#1A365D]">Requis</th>
                          <th className="text-left py-2 text-[#1A365D]">Exemple</th>
                        </tr>
                      </thead>
                      <tbody className="text-[#64748B]">
                        <tr className="border-b border-[#E2E8F0]">
                          <td className="py-2">Prenom</td>
                          <td className="py-2"><Badge className="bg-[#EF4444]/20 text-[#EF4444] border-0">Oui</Badge></td>
                          <td className="py-2">Marie</td>
                        </tr>
                        <tr className="border-b border-[#E2E8F0]">
                          <td className="py-2">Nom</td>
                          <td className="py-2"><Badge className="bg-[#EF4444]/20 text-[#EF4444] border-0">Oui</Badge></td>
                          <td className="py-2">Dupont</td>
                        </tr>
                        <tr className="border-b border-[#E2E8F0]">
                          <td className="py-2">Classe</td>
                          <td className="py-2"><Badge className="bg-[#EF4444]/20 text-[#EF4444] border-0">Oui</Badge></td>
                          <td className="py-2">Terminale A</td>
                        </tr>
                        <tr>
                          <td className="py-2">Email</td>
                          <td className="py-2"><Badge className="bg-[#64748B]/20 text-[#64748B] border-0">Non</Badge></td>
                          <td className="py-2">marie.dupont@ecole.fr</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="p-4 bg-[#F8F9FA] rounded-lg">
                  <h3 className="font-semibold text-[#1A365D] mb-2">Etapes d'import</h3>
                  <ol className="space-y-2 text-sm text-[#64748B] list-decimal list-inside">
                    <li>Preparez votre fichier Excel ou CSV</li>
                    <li>Cliquez sur "Importer des eleves" dans le tableau de bord</li>
                    <li>Selectionnez votre fichier</li>
                    <li>Verifiez l'apercu des donnees</li>
                    <li>Confirmez l'import</li>
                  </ol>
                  <p className="text-sm text-[#D4AF37] mt-3">
                    Les mots de passe sont generes automatiquement et envoyes par email si configure.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Moderation */}
          <TabsContent value="alerts">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Shield className="w-6 h-6 text-[#EF4444]" />
                  Moderation & Alertes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-[#64748B] leading-relaxed">
                  SophIA surveille automatiquement les conversations pour detecter les contenus inappropries. 
                  Vous etes alertes en temps reel.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#EF4444]/10 rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
                      Types d'alertes
                    </h3>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <Badge className="bg-[#EF4444] text-white border-0 text-xs">Critique</Badge>
                        Contenu tres inapproprie
                      </li>
                      <li className="flex items-start gap-2">
                        <Badge className="bg-[#F59E0B] text-white border-0 text-xs">Haute</Badge>
                        Langage grossier
                      </li>
                      <li className="flex items-start gap-2">
                        <Badge className="bg-[#3B82F6] text-white border-0 text-xs">Moyenne</Badge>
                        Comportement suspect
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-[#F8F9FA] rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-[#D4AF37]" />
                      Notifications
                    </h3>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Email immediat pour alertes critiques
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Resume quotidien optionnel
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Rapport hebdomadaire
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-[#1A365D]/5 rounded-lg">
                  <h3 className="font-semibold text-[#1A365D] mb-2">Actions possibles</h3>
                  <ul className="space-y-2 text-sm text-[#64748B]">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                      <strong>Marquer comme revue</strong> : L'alerte disparait de la liste active
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                      <strong>Voir le contexte</strong> : Consultez l'historique complet de la conversation
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                      <strong>Contacter l'eleve</strong> : Envoyez un message ou convoquez
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Settings className="w-6 h-6 text-[#8B5CF6]" />
                  Reglages
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="p-4 bg-[#8B5CF6]/10 rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#8B5CF6]" />
                      Configuration Email (SMTP)
                    </h3>
                    <p className="text-sm text-[#64748B] mb-3">
                      Configurez l'envoi d'emails pour les notifications automatiques.
                    </p>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                        Serveur SMTP : Adresse de votre serveur mail
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                        Port : Généralement 587 (TLS) ou 465 (SSL)
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                        Identifiants : Email et mot de passe du compte expéditeur
                      </li>
                    </ul>
                    <Badge className="mt-3 bg-[#22C55E]/20 text-[#22C55E] border-0">
                      Utilisez le bouton "Test" pour vérifier la configuration
                    </Badge>
                  </div>

                  <div className="p-4 bg-[#F8F9FA] rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <Building className="w-4 h-4 text-[#1A365D]" />
                      Gestion des établissements
                    </h3>
                    <p className="text-sm text-[#64748B]">
                      Si vous êtes administrateur multi-établissements, vous pouvez :
                    </p>
                    <ul className="space-y-2 text-sm text-[#64748B] mt-2">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Basculer entre établissements
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Créer de nouveaux établissements
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#22C55E]" />
                        Comparer les statistiques
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Back button */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate("/director")}
            className="bg-[#1A365D] hover:bg-[#132745]"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminManualPage;
